import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useViewMode } from '../contexts/ViewModeContext';
import CatalogGridView from '../components/Catalog/CatalogGridView';
import CatalogMagazineView from '../components/Catalog/CatalogMagazineView';
import productService from '../services/productService';

const Catalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedCollection, setSelectedCollection] = useState(searchParams.get('collection') || '');
  
  const { viewMode } = useViewMode();

  useEffect(() => {
    fetchCategories();
    fetchCollections();
  }, []);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedCollection]);

  const fetchCategories = async () => {
    try {
      const response = await productService.getCollections();
      
      // Collections might come as an object with a collections array
      const collectionsArray = response.data.collections || response.data;
      
      // Find subcollections of Joalharia (parent_id = 24002)
      const joalhariaSubcollections = collectionsArray.filter(col => col.parent_id === 24002);
      
      if (joalhariaSubcollections.length > 0) {
        // Fetch all products to count them per category
        const productsResponse = await productService.getProducts({ limit: 1000 });
        const allProducts = productsResponse.data || [];
        
        // Use subcollections from Joalharia as categories with actual product counts
        const categoriesFromCollections = joalhariaSubcollections.map(sub => {
          // Count products that belong to this category
          const productCount = allProducts.filter(product => {
            return product.collections?.some(col => col.name === sub.name) || 
                   product.category === sub.name;
          }).length;
          
          return {
            value: sub.name,
            label: sub.name,
            count: productCount
          };
        });
        
        // Sort categories alphabetically
        categoriesFromCollections.sort((a, b) => a.label.localeCompare(b.label));
        
        setCategories(categoriesFromCollections);
      } else {
        // Fallback - this shouldn't happen if Joalharia exists
        console.warn('No subcollections found for Joalharia (parent_id: 24002)');
        setCategories([]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]); // Set empty array on error
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await productService.getCollections();
      
      // Collections might come as an object with a collections array
      const collectionsArray = response.data.collections || response.data;
      
      // Get all collections that are NOT Joalharia and NOT subcollections of Joalharia
      const validCollections = collectionsArray.filter(col => 
        col.id !== 24002 && // Not Joalharia itself
        col.parent_id !== 24002 && // Not subcollections of Joalharia
        col.active !== false // Only active collections
      );
      
      // Extract collection names
      const collectionNames = validCollections.map(col => col.name);
      
      // Remove duplicates and sort
      const uniqueCollections = [...new Set(collectionNames)].sort();
      
      setCollections(uniqueCollections);
    } catch (err) {
      console.error('Error fetching collections:', err);
      setCollections([]); // Set empty array on error
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await productService.getProducts({ limit: 1000 });
      let filteredProducts = [...response.data];
      
      // Client-side filtering based on selected category/collection
      if (selectedCategory) {
        filteredProducts = filteredProducts.filter(product => {
          return product.collections?.some(col => 
            col.name === selectedCategory
          ) || product.category === selectedCategory;
        });
      }
      
      if (selectedCollection) {
        filteredProducts = filteredProducts.filter(product => {
          return product.collections?.some(col => 
            col.name === selectedCollection
          ) || product.collection === selectedCollection;
        });
      }
      
      setProducts(filteredProducts);
    } catch (err) {
      setError(err.message || 'Error loading products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category) {
      searchParams.set('category', category);
    } else {
      searchParams.delete('category');
    }
    setSearchParams(searchParams);
  };

  const handleCollectionChange = (collection) => {
    setSelectedCollection(collection);
    if (collection) {
      searchParams.set('collection', collection);
    } else {
      searchParams.delete('collection');
    }
    setSearchParams(searchParams);
  };

  // Render different views based on viewMode
  if (viewMode === 'magazine') {
    return <CatalogMagazineView products={products} />;
  }

  return (
    <CatalogGridView
      products={products}
      categories={categories}
      collections={collections}
      loading={loading}
      error={error}
      selectedCategory={selectedCategory}
      selectedCollection={selectedCollection}
      onCategoryChange={handleCategoryChange}
      onCollectionChange={handleCollectionChange}
    />
  );
};

export default Catalog;